import { I18n } from '@affine/i18n';

/** One notice per burst: further refusals within this window stay quiet. */
const NOTICE_INTERVAL_MS = 10_000;

type BackPressureListener = (retryAfterSeconds: number | undefined) => void;

const listeners = new Set<BackPressureListener>();
let lastNoticeAt = 0;

/**
 * Seconds until the server accepts the request again, or null when the answer
 * is no back pressure. Back pressure is 429, or 503 with Retry-After (the
 * server's busy answers, for example a saturated workspace database).
 */
export function backPressureRetryAfter(
  status: number,
  headers: Headers
): number | null | undefined {
  const retryAfter = headers.get('Retry-After');
  if (status !== 429 && !(status === 503 && retryAfter)) return null;
  const seconds = Number(retryAfter);
  return Number.isFinite(seconds) && seconds > 0
    ? Math.ceil(seconds)
    : undefined;
}

/**
 * The request layer reports back pressure of a request the user started;
 * the app shell shows the notice. At most one report per burst.
 */
export function reportBackPressure(
  retryAfterSeconds: number | undefined
): void {
  const now = Date.now();
  if (now - lastNoticeAt < NOTICE_INTERVAL_MS) return;
  lastNoticeAt = now;
  for (const listener of listeners) listener(retryAfterSeconds);
}

export function onBackPressure(listener: BackPressureListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Title and text of the notice: understanding instead of a refusal. */
export function backPressureNoticeContent(
  retryAfterSeconds: number | undefined
): { title: string; message: string } {
  return {
    title: I18n['com.streamlain.back-pressure.title'](),
    message:
      retryAfterSeconds === undefined
        ? I18n['com.streamlain.back-pressure.message-soon']()
        : I18n['com.streamlain.back-pressure.message']({
            seconds: String(retryAfterSeconds),
          }),
  };
}

/** Test seam: the next report reaches the listeners again. */
export function resetBackPressureNotice(): void {
  lastNoticeAt = 0;
}
